// ONE walk of a bulk drop's spatial members — zip/kml/kmz/geojson plus a loose shapefile
// set (foo.shp + foo.dbf dropped as siblings). Sample, zone, and yield lanes share this so a
// dbf is never a second unidentified file and a bare.shp is actually readable.
import {
  layersOf,
  fileBytes,
  MAX_INFLATED_BYTES,
  MAX_SCAN_BYTES,
  type Layer,
} from "./boundaryScan";
import { readShapefile } from "./shpRead";

export const fileKeyOf = (f: { name: string; size: number }): string =>
  `${f.name}:${f.size}`;

export const isCsvName = (n: string) => /\.csv$/i.test(n);
export const isArchiveGeoName = (n: string) =>
  /\.(zip|kmz|kml|geojson|json)$/i.test(n);
export const isShpSidecarName = (n: string) => /\.(dbf|prj|shx|cpg)$/i.test(n);
export const isShpName = (n: string) => /\.shp$/i.test(n);
export const isSpatialName = (n: string) =>
  isArchiveGeoName(n) || isShpName(n) || isShpSidecarName(n);

export type DropFile = { name: string; size: number } & Blob;

export interface DropLayer extends Layer {
  /* * the manifest file that owns this layer (the.shp, or the zip/kml/geojson) */
  file: string;
  /* * every sibling that must annotate together (shp+dbf+prj, or the single archive) */
  fileKeys: string[];
}

function shpStem(name: string): string {
  const base = name.replace(/\\/g, "/").split("/").pop() ?? name;
  return base.toLowerCase().replace(/\.(shp|dbf|prj|shx|cpg)$/i, "");
}

interface ShpGroup {
  stem: string;
  files: DropFile[];
  shp?: DropFile;
  dbf?: DropFile;
  prj?: DropFile;
}

function groupShapefiles(files: DropFile[]): Map<string, ShpGroup> {
  const by = new Map<string, ShpGroup>();
  for (const f of files) {
    if (!/\.(shp|dbf|prj|shx|cpg)$/i.test(f.name)) continue;
    const stem = shpStem(f.name);
    const g = by.get(stem) ?? { stem, files: [] };
    g.files.push(f);
    const n = f.name.toLowerCase();
    if (n.endsWith(".shp")) g.shp = f;
    else if (n.endsWith(".dbf")) g.dbf = f;
    else if (n.endsWith(".prj")) g.prj = f;
    by.set(stem, g);
  }
  return by;
}

/* *.shp files in the drop that have no matching.dbf — compute's attach refuses these as `shapefile_missing_dbf` */
export function shapefilesMissingDbf(files: DropFile[]): DropFile[] {
  return [...groupShapefiles(files).values()]
    .filter((g) => g.shp && !g.dbf)
    .map((g) => g.shp!);
}

/* * sidecar parts of a shapefile that already has a.shp in the drop — skip as their own files */
export function shapefileSidecarKeys(files: DropFile[]): Set<string> {
  const out = new Set<string>();
  for (const g of groupShapefiles(files).values()) {
    if (!g.shp) continue;
    for (const f of g.files) if (!isShpName(f.name)) out.add(fileKeyOf(f));
  }
  return out;
}

async function prjText(f: DropFile | undefined): Promise<string | undefined> {
  if (!f) return undefined;
  try {
    return new TextDecoder().decode(await fileBytes(f));
  } catch {
    return undefined;
  }
}

/*
 *
 * Every readable spatial layer in the drop. Never throws — an unreadable member simply isn't
 * returned (the caller decides whether that's a `left` reason or silence).
 */
export async function layersFromDrop(files: DropFile[]): Promise<DropLayer[]> {
  const out: DropLayer[] = [];
  for (const f of files) {
    if (!isArchiveGeoName(f.name)) continue;
    if (f.size > MAX_SCAN_BYTES) continue;
    try {
      const layers = await layersOf(f.name, await fileBytes(f), {
        left: MAX_INFLATED_BYTES,
      });
      const keys = [fileKeyOf(f)];
      for (const layer of layers)
        out.push({ ...layer, file: f.name, fileKeys: keys });
    } catch {
      /* unreadable — other lanes name it */
    }
  }
  for (const g of groupShapefiles(files).values()) {
    if (!g.shp || g.shp.size > MAX_SCAN_BYTES) continue;
    try {
      const shp = await fileBytes(g.shp);
      const dbf = g.dbf ? await fileBytes(g.dbf) : undefined;
      const prj = await prjText(g.prj);
      const features = readShapefile({ shp, dbf, prj });
      out.push({
        source: g.shp.name,
        features,
        file: g.shp.name,
        fileKeys: g.files.map(fileKeyOf),
      });
    } catch {
      /* unreadable shapefile set */
    }
  }
  return out;
}

export function flattenPointFeatures(features: GeoJSON.Feature[]): {
  pts: GeoJSON.Feature[];
  multiMember: boolean;
} {
  let multiMember = false;
  const pts = features.flatMap((ft) => {
    if (ft?.geometry?.type === "Point") return [ft];
    if (ft?.geometry?.type === "MultiPoint") {
      const c = (ft.geometry as GeoJSON.MultiPoint).coordinates;
      if (c.length === 1)
        return [
          {
            ...ft,
            geometry: { type: "Point", coordinates: c[0]! },
          } as GeoJSON.Feature,
        ];
      multiMember = true;
    }
    return [];
  });
  return { pts, multiMember };
}

export function polygonFeaturesOf(
  features: GeoJSON.Feature[],
): GeoJSON.Feature[] {
  return features.filter(
    (ft) =>
      ft?.geometry?.type === "Polygon" || ft?.geometry?.type === "MultiPolygon",
  );
}

/* * first vertex — the same probe dropIdentify uses on zone rings */
export function polygonProbe(ft: GeoJSON.Feature): [number, number] | null {
  const g = ft.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  if (!g) return null;
  const ring = g.type === "Polygon" ? g.coordinates[0] : g.coordinates[0]?.[0];
  const c = ring?.[0];
  if (!c || !Number.isFinite(c[0]) || !Number.isFinite(c[1])) return null;
  return [c[0]!, c[1]!];
}

export function pseudoCsvOf(features: GeoJSON.Feature[]): {
  headers: string[];
  rows: string[][];
} {
  const headers = [
    ...new Set(features.flatMap((ft) => Object.keys(ft.properties ?? {}))),
  ];
  const rows = features.map((ft) =>
    headers.map((k) =>
      String((ft.properties as Record<string, unknown> | null)?.[k] ?? ""),
    ),
  );
  return { headers, rows };
}
